/**
 * WorkOrders — Deep Data management table view
 *
 * Full-featured sortable/filterable table for all work orders
 * with stats header and bulk actions.
 */

import { useEffect, useState, useCallback, useRef } from "react";
import {
	Table,
	Tag,
	Button,
	Select,
	Tooltip,
	message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
	PlusOutlined,
	ReloadOutlined,
	WarningOutlined,
	ToolOutlined,
	ClockCircleOutlined,
	SyncOutlined,
	ExclamationCircleOutlined,
	CheckCircleOutlined,
	DollarOutlined,
	BulbOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import {
	listWorkOrders,
	getWorkOrderStats,
	type WorkOrder,
} from "@/services/work-order-service";
import { useWorkOrderStore } from "@/stores/work-order-store";
import "./WorkOrders.scss";

const STATUS_COLORS: Record<string, string> = {
	OPEN: "blue",
	ASSIGNED: "cyan",
	IN_PROGRESS: "processing",
	ON_HOLD: "warning",
	COMPLETED: "success",
	CLOSED: "default",
	CANCELLED: "error",
	DRAFT: "default",
};

const PRIORITY_COLORS: Record<number, string> = {
	1: "red",
	2: "orange",
	3: "gold",
	4: "blue",
	5: "default",
};

const PRIORITY_LABELS: Record<number, string> = {
	1: "Emergency",
	2: "Urgent",
	3: "High",
	4: "Medium",
	5: "Low",
};

const WorkOrders = () => {
	const navigate = useNavigate();
	const {
		workOrders,
		total,
		filters,
		loading,
		stats,
		setWorkOrders,
		setFilters,
		setLoading,
		setStats,
	} = useWorkOrderStore();

	const tableContainerRef = useRef<HTMLDivElement>(null);
	const [tableScrollY, setTableScrollY] = useState<number>(400);

	// Dynamically calculate available height for the table so it fills the viewport
	useEffect(() => {
		const calculateScrollY = () => {
			if (tableContainerRef.current) {
				const containerTop = tableContainerRef.current.getBoundingClientRect().top;
				// offset accounts for: stats bar, toolbar, table header, pagination, footer
				const offset = 162;
				const available = window.innerHeight - containerTop - offset;
				setTableScrollY(Math.max(available, 200));
			}
		};
		// Small delay to ensure the DOM has painted (stats bar shifts content)
		const timer = setTimeout(calculateScrollY, 50);
		window.addEventListener("resize", calculateScrollY);
		return () => {
			clearTimeout(timer);
			window.removeEventListener("resize", calculateScrollY);
		};
	}, [stats]); // recalculate when stats load (shifts content down)

	const fetchData = useCallback(async () => {
		setLoading(true);
		try {
			const [listResult, statsResult] = await Promise.all([
				listWorkOrders({
					status: filters.status ?? "",
					priority: filters.priority ?? "",
					workType: filters.workType ?? "",
					page: filters.page,
					limit: filters.limit,
					sortBy: filters.sortBy,
					sortOrder: filters.sortOrder,
					overdue: filters.overdue ?? false,
				}),
				getWorkOrderStats(),
			]);
			setWorkOrders(listResult.workOrders, listResult.total);
			setStats(statsResult);
		} catch (error) {
			console.error("Failed to fetch work orders:", error);
			message.error("Failed to load work orders");
		} finally {
			setLoading(false);
		}
	}, [filters]);

	useEffect(() => {
		fetchData();
	}, [fetchData]);

	const columns: ColumnsType<WorkOrder> = [
		{
			title: "WO #",
			dataIndex: "workOrderNumber",
			key: "workOrderNumber",
			width: 140,
			render: (text: string) => (
				<span style={{ fontFamily: "monospace", fontWeight: 600 }}>{text}</span>
			),
		},
		{
			title: "Title",
			dataIndex: "title",
			key: "title",
			ellipsis: true,
		},
		{
			title: "Location",
			dataIndex: "locationName",
			key: "locationName",
			width: 160,
			ellipsis: true,
		},
		{
			title: "Type",
			dataIndex: "workType",
			key: "workType",
			width: 110,
			render: (type: string) => <Tag>{type}</Tag>,
		},
		{
			title: "Priority",
			dataIndex: "priority",
			key: "priority",
			width: 100,
			sorter: true,
			render: (p: number) => (
				<Tag color={PRIORITY_COLORS[p]}>
					P{p} {PRIORITY_LABELS[p]}
				</Tag>
			),
		},
		{
			title: "Status",
			dataIndex: "status",
			key: "status",
			width: 120,
			render: (s: string) => (
				<Tag color={STATUS_COLORS[s]}>{s.replace(/_/g, " ")}</Tag>
			),
		},
		{
			title: "Assignee",
			key: "assignee",
			width: 140,
			ellipsis: true,
			render: (_: unknown, wo: WorkOrder) =>
				wo.assignedUserName ?? wo.assignedVendorName ?? (
					<span style={{ color: "#8c8c8c" }}>Unassigned</span>
				),
		},
		{
			title: "SLA",
			key: "sla",
			width: 70,
			align: "center",
			render: (_: unknown, wo: WorkOrder) =>
				wo.isSlaBreached ? (
					<Tooltip title="SLA Breached">
						<WarningOutlined style={{ color: "#ff4d4f", fontSize: 16 }} />
					</Tooltip>
				) : (
					<span style={{ color: "#52c41a" }}>OK</span>
				),
		},
		{
			title: "Cost",
			dataIndex: "actualCost",
			key: "actualCost",
			width: 100,
			align: "right",
			render: (cost: number) =>
				cost > 0 ? `$${Number(cost).toLocaleString()}` : "—",
		},
		{
			title: "Created",
			dataIndex: "createdAt",
			key: "createdAt",
			width: 120,
			sorter: true,
			render: (d: string) =>
				new Date(d).toLocaleDateString(undefined, {
					month: "short",
					day: "numeric",
					year: "2-digit",
				}),
		},
	];

	return (
		<div className="work-orders-page" ref={tableContainerRef}>
			{/* Stats Header */}
			{stats && (
				<div className="wo-stats-bar">
					<div className="wo-stat-card wo-stat-open">
						<div className="wo-stat-icon"><ClockCircleOutlined /></div>
						<div className="wo-stat-body">
							<span className="wo-stat-num">{stats.open}</span>
							<span className="wo-stat-lbl">Open</span>
						</div>
					</div>
					<div className="wo-stat-card wo-stat-progress">
						<div className="wo-stat-icon"><SyncOutlined /></div>
						<div className="wo-stat-body">
							<span className="wo-stat-num">{stats.inProgress}</span>
							<span className="wo-stat-lbl">In Progress</span>
						</div>
					</div>
					<div className="wo-stat-card wo-stat-overdue">
						<div className="wo-stat-icon"><ExclamationCircleOutlined /></div>
						<div className="wo-stat-body">
							<span className="wo-stat-num">{stats.overdue}</span>
							<span className="wo-stat-lbl">Overdue</span>
						</div>
					</div>
					<div className="wo-stat-card wo-stat-completed">
						<div className="wo-stat-icon"><CheckCircleOutlined /></div>
						<div className="wo-stat-body">
							<span className="wo-stat-num">{stats.completed + stats.closed}</span>
							<span className="wo-stat-lbl">Completed</span>
						</div>
					</div>
					<div className="wo-stat-card wo-stat-cost">
						<div className="wo-stat-icon"><DollarOutlined /></div>
						<div className="wo-stat-body">
							<span className="wo-stat-num">${Number(stats.totalCost).toLocaleString()}</span>
							<span className="wo-stat-lbl">Total Cost</span>
						</div>
					</div>
				</div>
			)}

			{/* Toolbar */}
			<div className="wo-toolbar">
				<div className="wo-toolbar-left">
					<h2><ToolOutlined /> Work Orders</h2>
				</div>
				<div className="wo-toolbar-right">
					<Select
						placeholder="Status"
						allowClear
						style={{ width: 140 }}
						value={filters.status}
						onChange={(v) => setFilters({ status: v, page: 1 })}
						options={[
							{ value: "OPEN", label: "Open" },
							{ value: "ASSIGNED", label: "Assigned" },
							{ value: "IN_PROGRESS", label: "In Progress" },
							{ value: "ON_HOLD", label: "On Hold" },
							{ value: "COMPLETED", label: "Completed" },
							{ value: "CLOSED", label: "Closed" },
						]}
					/>
					<Select
						placeholder="Priority"
						allowClear
						style={{ width: 130 }}
						value={filters.priority}
						onChange={(v) => setFilters({ priority: v, page: 1 })}
						options={[
							{ value: 1, label: "P1 Emergency" },
							{ value: 2, label: "P2 Urgent" },
							{ value: 3, label: "P3 High" },
							{ value: 4, label: "P4 Medium" },
							{ value: 5, label: "P5 Low" },
						]}
					/>
					<Select
						placeholder="Type"
						allowClear
						style={{ width: 130 }}
						value={filters.workType}
						onChange={(v) => setFilters({ workType: v, page: 1 })}
						options={[
							{ value: "EMERGENCY", label: "Emergency" },
							{ value: "CORRECTIVE", label: "Corrective" },
							{ value: "PREVENTIVE", label: "Preventive" },
							{ value: "INSPECTION", label: "Inspection" },
						]}
					/>
					<Button icon={<ReloadOutlined />} onClick={fetchData}>
						Refresh
					</Button>
					<Button icon={<BulbOutlined />} onClick={() => navigate("/work-orders/triage")} style={{ borderColor: "#fa8c16", color: "#fa8c16" }}>
						AI Triage
					</Button>
					<Button onClick={() => navigate("/work-orders/vendors")}>
						Vendors
					</Button>
					<Button
						type="primary"
						icon={<PlusOutlined />}
						onClick={() => navigate("/work-orders/new")}
					>
						New Work Order
					</Button>
				</div>
			</div>

			{/* Table */}
			<Table
				columns={columns}
				dataSource={workOrders}
				rowKey="id"
				loading={loading}
				pagination={{
					current: filters.page,
					pageSize: filters.limit,
					total,
					showSizeChanger: true,
					showTotal: (t) => `${t} work orders`,
					onChange: (page, pageSize) =>
						setFilters({ page, limit: pageSize }),
				}}
				onChange={(_pagination, _filters, sorter) => {
					if (!Array.isArray(sorter) && sorter.field) {
						setFilters({
							sortBy: sorter.field as string,
							sortOrder: sorter.order === "ascend" ? "ASC" : "DESC",
							page: 1,
						});
					}
				}}
				onRow={(record) => ({
					onClick: () => navigate(`/work-orders/${record.id}`),
					style: { cursor: "pointer" },
				})}
				scroll={{ x: 1200, y: tableScrollY }}
				size="middle"
			/>
		</div>
	);
};

export { WorkOrders };
